import { type NoteTemplateAttributes } from './generated/NoteTemplateAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import Errors from '../Errors';
import Fragment from '../Fragment';
import { model, modelObject } from '../modelBuilder';
import Page from './Page';

interface NoteTemplate extends NoteTemplateAttributes {}

@model
class NoteTemplate extends ApplicationModel {
  // Start auto-generated
  static typename: 'NoteTemplate' = 'NoteTemplate';
  get typename(): 'NoteTemplate' {
    return 'NoteTemplate';
  }

  static internalTypename: 'NoteTemplate' = 'NoteTemplate';
  get internalTypename(): 'NoteTemplate' {
    return 'NoteTemplate';
  }

  static inputType = 'NoteTemplateAttributes!';

  static filterType = 'NoteTemplateFilters';
  static filters = {
    id: '[ID!]',
    projectId: 'ID',
    documentType: '[DocumentTypeEnum!]',
    isPersonal: 'Boolean',
  };

  static fields = {
    categories: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    documentType: attr({ readable: true }),
    emojiValue: attr({ readable: true }),
    hasLongDescription: attr({ readable: true }),
    id: attr({ writable: true, readable: true }),
    isGlobal: attr({ readable: true }),
    isGlobalTemplate: attr({ writable: true, readable: false }),
    isPersonal: attr({ writable: true, readable: true }),
    meetingAttendeesCount: attr({ readable: true }),
    name: attr({ readable: true }),
    position: attr({ writable: true, readable: false }),
    screenshot: attr({ readable: true }),
    slug: attr({ readable: true }),
    thumbnail: attr({ readable: true }),
    thumbnailUrl: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    watchersCount: attr({ readable: true }),
    whiteboardId: attr({ readable: true }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({ readable: true, typename: 'Note' }),
    exampleDescription: attr.belongsTo({ readable: true, typename: 'Note' }),
    exampleWhiteboard: attr.belongsTo({
      readable: true,
      typename: 'Whiteboard',
    }),
    guideDescription: attr.belongsTo({ readable: true, typename: 'Note' }),
    longDescription: attr.belongsTo({ readable: true, typename: 'Note' }),
    parent: attr.belongsTo({ writeShape: ['id'], readable: false }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    project: attr.belongsTo({ writeShape: ['id'], readable: false }),
    shortDescription: attr.belongsTo({ readable: true, typename: 'Note' }),
    user: attr.belongsTo({ readable: true, typename: 'User' }),
    whiteboard: attr.belongsTo({ readable: true, typename: 'Whiteboard' }),
    meetingAttendees: attr.hasMany({ readable: true }),
    watchers: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  static async createPage({
    id,
    projectId,
    parentId,
    position = 0,
    isGlobalTemplate = false,
  }) {
    if (typeof position === 'string') {
      position = parseInt(position, 10);
    }
    const fragment = new Fragment('createPageFromTemplate')
      .argument(
        'attributes',
        {
          id,
          project: { id: projectId },
          parent: { id: parentId },
          position,
          isGlobalTemplate,
        },
        'NoteTemplateAttributes!'
      )
      .fragment(
        new Fragment('page')
          .attr(
            'id',
            'name',
            'path',
            'referenceNum',
            'position',
            'parent { id }',
            'documentType'
          )
          .fragment(new Fragment('project').attr('id'))
      );

    const { createPageFromTemplate: data } = await this.mutate(fragment);
    const result = modelObject<{ page: Page } & Aha.RecordErrors>(data);
    const errors = new Errors(result.errors);

    if (errors.isEmpty) {
      return result.page;
    } else {
      throw new Error(errors.fullMessages.join(', '));
    }
  }

  get nameOrDefault() {
    return this.name || 'Untitled Template';
  }

  get availableThumbnail() {
    if (this.thumbnail) {
      return 'thumbnail';
    } else if (this.thumbnailUrl) {
      return 'screenshot';
    } else {
      return 'placeholder';
    }
  }

  get editorContent() {
    if ('editorContent' in this.description) {
      return this.description.editorContent;
    } else if (this.whiteboard?.editorContent) {
      return this.whiteboard.editorContent;
    } else if (this.description?.editorProperties) {
      return this.description.editorProperties.value;
    } else {
      return null;
    }
  }

  get isMeeting() {
    return Page.DOCUMENT_TYPE_MEETING.includes(this.documentType);
  }
}

export default NoteTemplate;
